import { Ok, Result } from 'ts-res';
import { Parser } from 'src/utilities';
import { KameleoonData, PageViewDataType } from 'src/kameleoonData/types';
import { KameleoonError } from 'src/kameleoonError';
import { EvaluationDataType, MatchType } from 'src/targeting/types';
import { ConditionDataType, ICondition } from './types';
import { KameleoonLogger } from 'src/logging';

export class PreviousPage implements ICondition {
  private conditionValue?: string | null;
  private matchType?: MatchType | null;

  constructor({ matchType, url }: ConditionDataType) {
    this.conditionValue = url;
    this.matchType = matchType;
  }

  public evaluate({
    targetingData,
  }: EvaluationDataType): Result<boolean, KameleoonError> {
    const pageViewsData = targetingData?.[KameleoonData.PageView];

    if (!pageViewsData) {
      return Ok(false);
    }

    // --- Note ---
    // There is more concise way to find previous latest page, but it's less performant,
    // here we only ever check two latest timestamps of page and use one loop
    // The second latest can't be equal to the latest
    const secondLatestPageView = this.getSecondLatestPageView(
      Object.values(pageViewsData),
    );

    if (!secondLatestPageView) {
      return Ok(false);
    }

    const url = secondLatestPageView.urlAddress;

    return Ok(this.checkCondition(url));
  }

  private checkCondition(url: string): boolean {
    if (!this.matchType || !this.conditionValue) {
      return false;
    }

    switch (this.matchType) {
      case MatchType.CONTAINS:
        return url.includes(this.conditionValue);
      case MatchType.EXACT:
        return url === this.conditionValue;
      case MatchType.REGULAR_EXPRESSION:
        return Parser.parseRegExp(this.conditionValue).test(url);
      default:
        KameleoonLogger.error`Unexpected comparing operation for PreviousPage condition: ${this.matchType}`;
        return false;
    }
  }

  private getSecondLatestPageView(
    pageViewsData: PageViewDataType[],
  ): PageViewDataType | undefined {
    if (pageViewsData.length < 2) {
      return undefined;
    }

    // --- Note ---
    // Arguments and variables named with abbreviations for better readability
    // `second` in this context is `second to latest`
    let latestPV: PageViewDataType | undefined;
    let secondPV: PageViewDataType | undefined;

    pageViewsData.forEach((currentPV) => {
      const currentLatestTs =
        currentPV.timestamps[currentPV.timestamps.length - 1].time;

      // -- Initial check
      if (!latestPV) {
        latestPV = currentPV;
        return;
      }

      const latestTs =
        latestPV.timestamps[latestPV!.timestamps.length - 1].time;

      // -- Covering a case where latest needs to be updated
      if (currentLatestTs > latestTs) {
        secondPV = latestPV;
        latestPV = currentPV;

        return;
      }

      // -- Covering a case where only second needs to be updated
      const secondTs = secondPV
        ? secondPV.timestamps[secondPV.timestamps.length - 1].time
        : 0;

      // -- Second may need to be updated
      if (currentLatestTs > secondTs) {
        secondPV = currentPV;
      }
    });

    return secondPV;
  }
}
